import { apiClient } from './client';

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  key?: string;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  syncFailureAlerts: boolean;
  weeklyDigest: boolean;
  workflowAlerts: boolean;
  securityAlerts: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
}

export async function updateProfile(data: {
  name?: string;
  email?: string;
}): Promise<{ user: UserProfile }> {
  return apiClient.patch('/auth/profile', data);
}

export async function changePassword(data: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ success: boolean; message: string }> {
  return apiClient.patch('/auth/password', data);
}

export async function getApiKeys(): Promise<{ apiKeys: ApiKey[] }> {
  return apiClient.get('/auth/api-keys');
}

export async function createApiKey(data: {
  name: string;
  scopes?: string[];
  expiresInDays?: number;
}): Promise<{ apiKey: ApiKey; message: string }> {
  return apiClient.post('/auth/api-keys', data);
}

export async function deleteApiKey(id: string): Promise<{ success: boolean }> {
  return apiClient.delete(`/auth/api-keys/${id}`);
}

export async function getNotificationPreferences(): Promise<{ preferences: NotificationPreferences }> {
  return apiClient.get('/auth/notifications');
}

export async function updateNotificationPreferences(
  data: Partial<NotificationPreferences>
): Promise<{ preferences: NotificationPreferences }> {
  return apiClient.patch('/auth/notifications', data);
}

/** Toggle an opt-in section (e.g. AI Visibility) for the current user. Admin-only. */
export async function updateOptionalFeature(data: {
  feature: string;
  enabled: boolean;
}): Promise<{ access: string[] }> {
  return apiClient.patch('/auth/optional-features', data);
}

// Google Account
export interface GoogleAccountStatus {
  connected: boolean;
  googleEmail?: string;
  status?: string;
  connectedAt?: string;
}

export async function getGoogleAccountStatus(): Promise<GoogleAccountStatus> {
  return apiClient.get('/google-account/status');
}

export async function getGoogleAccountAuthUrl(): Promise<{ url: string }> {
  return apiClient.get('/google-account/auth-url');
}

export async function exchangeGoogleAccountCode(code: string, state: string): Promise<{ success: boolean; googleEmail: string }> {
  return apiClient.post('/google-account/callback', { code, state });
}

export async function disconnectGoogleAccount(): Promise<{ success: boolean }> {
  return apiClient.delete('/google-account');
}
